import { ReactNode, useState } from "react";
import { NavLink } from "react-router-dom";
import PhoneProviderSelector from "./crm/PhoneProviderSelector";
import GlobalDialer from "./crm/GlobalDialer";
import { useAuth } from "../context/AuthContext";
import {
  COUNTRIES,
  COUNTRY_INFO,
  Country,
  useMarket,
} from "../context/MarketContext";
import {
  FlagES,
  FlagFR,
  FlagIT,
  IconBuilding,
  IconCard,
  IconClipboardList,
  IconDollar,
  IconFile,
  IconHeartPulse,
  IconHistory,
  IconLifebuoy,
  IconRenew,
  IconSearch,
  IconTicket,
  IconPhone,
  IconRotateRight,
  IconWarning,
} from "./Icons";
import { useMsOpsEnabled } from "../api/msops";

type IconComponent = (props: { style?: React.CSSProperties }) => JSX.Element;

const FLAGS: Record<Country, (props: { style?: React.CSSProperties }) => JSX.Element> = {
  ES: FlagES,
  IT: FlagIT,
  FR: FlagFR,
};

const STORAGE_KEY = "admin:sidebar:collapsed";

export default function Layout({ children }: { children: ReactNode }) {
  const { logout } = useAuth();
  const { countries, toggleCountry } = useMarket();
  // Se oculta sólo tras confirmar el 404 del passthrough; hasta entonces el
  // enlace se muestra (ver la nota del feature flag en `api/msops.ts`).
  const msOpsEnabled = useMsOpsEnabled();
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(STORAGE_KEY) === "true",
  );

  const toggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(STORAGE_KEY, String(next));
      return next;
    });
  };

  const width = collapsed ? 56 : 220;

  return (
    <div
      style={{
        display: "flex",
        minHeight: "100vh",
        background: "#f8f9fb",
      }}
    >
      <aside
        style={{
          width,
          background: "#2A3F54",
          color: "white",
          transition: "width 0.18s ease",
          display: "flex",
          flexDirection: "column",
          position: "sticky",
          top: 0,
          height: "100vh",
          overflow: "hidden",
          flexShrink: 0,
        }}
      >
        <div
          style={{
            padding: collapsed ? "0.85rem 0" : "0.85rem 1rem",
            display: "flex",
            alignItems: "center",
            justifyContent: collapsed ? "center" : "space-between",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
            minHeight: 56,
          }}
        >
          {!collapsed && (
            <h1
              style={{
                margin: 0,
                fontSize: "0.95rem",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
            >
              Life5 Admin
            </h1>
          )}
          <button
            type="button"
            onClick={toggle}
            title={collapsed ? "Expandir menú" : "Colapsar menú"}
            aria-label={collapsed ? "Expandir menú" : "Colapsar menú"}
            style={{
              background: "transparent",
              color: "white",
              border: "none",
              padding: "0.25rem 0.4rem",
              cursor: "pointer",
              borderRadius: "0.3rem",
              fontSize: "1.05rem",
              lineHeight: 1,
            }}
          >
            {collapsed ? "›" : "‹"}
          </button>
        </div>

        <nav
          style={{
            flex: 1,
            padding: "0.75rem 0",
            display: "flex",
            flexDirection: "column",
            gap: "0.1rem",
          }}
        >
          <SidebarLink
            to="/operations/leads/search"
            Icon={IconSearch}
            label="Búsqueda global"
            collapsed={collapsed}
          />
          <SidebarLink
            to="/operations/onboarding"
            Icon={IconClipboardList}
            label="Onboarding"
            collapsed={collapsed}
          />
          <SidebarLink
            to="/operations/historical"
            Icon={IconHistory}
            label="Historical"
            collapsed={collapsed}
          />
          <SidebarLink
            to="/operations/renewals-it"
            Icon={IconRenew}
            label="Renovaciones IT"
            collapsed={collapsed}
          />
          <SidebarLink
            to="/operations/subscriptions"
            Icon={IconCard}
            label="Suscripciones"
            collapsed={collapsed}
          />
          <SidebarLink
            to="/operations/collections"
            Icon={IconDollar}
            label="Cobranza"
            collapsed={collapsed}
          />
          <SidebarLink
            to="/operations/impagos"
            Icon={IconWarning}
            label="Impagos"
            collapsed={collapsed}
          />
          {/*
            Recobros vive junto a Impagos porque son las dos caras del mismo
            trabajo: Impagos lista las FACTURAS impagadas del admin-api,
            Recobros los EPISODIOS del motor de cadencias (ms-ops). El enlace
            desaparece en cuanto el passthrough `/api/admin/ops/*` devuelve
            404, señal de que ms-ops no está configurado en este entorno.
          */}
          {msOpsEnabled && (
            <SidebarLink
              to="/operations/recobros/cartera"
              Icon={IconRotateRight}
              label="Recobros"
              collapsed={collapsed}
            />
          )}
          {/*
            La cola de tareas SIGUE teniendo enlace propio: es el trabajo
            diario de una operadora, que entra directa a vaciarla, y esconderla
            tras otra pantalla costaría dos clics en lo que más se usa. Lo que
            cambió (2026-08-10) es que ya no es una isla: Recobros es una
            sección con pestañas —Cartera · Mi cola · Casos · Condonados— y
            desde cualquiera de ellas se llega a las demás en un clic.
          */}
          {msOpsEnabled && (
            <SidebarLink
              to="/operations/recobros/tareas"
              Icon={IconClipboardList}
              label="Mis tareas de recobro"
              collapsed={collapsed}
            />
          )}
          <SidebarLink
            to="/operations/payment-methods/expiring"
            Icon={IconCard}
            label="Tarjetas caducando"
            collapsed={collapsed}
          />
          <SidebarLink
            to="/operations/cancellation-requests"
            Icon={IconFile}
            label="Cancelaciones"
            collapsed={collapsed}
          />
          <SidebarLink
            to="/operations/coupons"
            Icon={IconTicket}
            label="Cupones"
            collapsed={collapsed}
          />
          <SidebarLink
            to="/operations/promos"
            Icon={IconDollar}
            label="Promos"
            collapsed={collapsed}
          />
          <SidebarLink
            to="/operations/tickets"
            Icon={IconLifebuoy}
            label="Tickets"
            collapsed={collapsed}
          />
          <SidebarLink
            to="/operations/calls"
            Icon={IconPhone}
            label="Llamadas"
            collapsed={collapsed}
          />
          <SidebarLink
            to="/brokers"
            Icon={IconBuilding}
            label="Brokers"
            collapsed={collapsed}
          />
          <SidebarLink
            to="/health"
            Icon={IconHeartPulse}
            label="Health"
            collapsed={collapsed}
          />
        </nav>
      </aside>

      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
        }}
      >
        <header
          style={{
            background: "white",
            padding: "0.65rem 1.5rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: "1rem",
            borderBottom: "1px solid #e9ecef",
            minHeight: 56,
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "0.35rem",
              alignItems: "center",
            }}
            role="group"
            aria-label="Países activos"
          >
            {COUNTRIES.map((c) => (
              <CountryToggle
                key={c}
                country={c}
                active={countries.includes(c)}
                onToggle={toggleCountry}
              />
            ))}
          </div>
          <PhoneProviderSelector />
          <GlobalDialer />
          <button onClick={logout}>Salir</button>
        </header>

        <main style={{ flex: 1 }}>{children}</main>
      </div>
    </div>
  );
}

function CountryToggle({
  country,
  active,
  onToggle,
}: {
  country: Country;
  active: boolean;
  onToggle: (c: Country) => void;
}) {
  const info = COUNTRY_INFO[country];
  const Flag = FLAGS[country];
  return (
    <button
      type="button"
      onClick={() => onToggle(country)}
      title={active ? `Desactivar ${info.label}` : `Activar ${info.label}`}
      aria-pressed={active}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.4rem",
        padding: "0.3rem 0.7rem",
        borderRadius: "999px",
        border: active ? "1px solid #0069d9" : "1px solid #dee2e6",
        background: active ? "#0069d9" : "white",
        color: active ? "white" : "#1f2933",
        fontSize: "0.78rem",
        fontWeight: 600,
        cursor: "pointer",
      }}
    >
      <Flag
        style={{
          boxShadow: active
            ? "0 0 0 1px rgba(255,255,255,.4)"
            : "0 0 0 1px rgba(0,0,0,.08)",
        }}
      />
      <span>{country}</span>
    </button>
  );
}

function SidebarLink({
  to,
  Icon,
  label,
  collapsed,
}: {
  to: string;
  Icon: IconComponent;
  label: string;
  collapsed: boolean;
}) {
  return (
    <NavLink
      to={to}
      title={collapsed ? label : undefined}
      style={({ isActive }) => ({
        color: "white",
        textDecoration: "none",
        padding: collapsed ? "0.65rem 0" : "0.65rem 1rem",
        display: "flex",
        alignItems: "center",
        justifyContent: collapsed ? "center" : "flex-start",
        gap: collapsed ? 0 : "0.7rem",
        fontSize: "0.88rem",
        fontWeight: 500,
        background: isActive ? "rgba(255,255,255,0.1)" : "transparent",
        borderLeft: isActive
          ? "3px solid #0069d9"
          : "3px solid transparent",
        transition: "background 0.15s ease",
        whiteSpace: "nowrap",
        overflow: "hidden",
      })}
    >
      <Icon style={{ width: "1.15rem", height: "1.15rem", fill: "currentColor" }} />
      {!collapsed && <span>{label}</span>}
    </NavLink>
  );
}
